import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Lock, KeyRound, MapPin, Sparkles, ArrowRight, Trophy } from "lucide-react";
import OGMeta from "@/components/OGMeta";
import HuntZoneMap from "@/components/HuntZoneMap";
import GpsHintButton from "@/components/GpsHintButton";
import { PrizeTypeBadge, prizeMeta } from "@/components/PrizeDisplay";
import { SkeletonGrid } from "@/components/Skeleton";
import PostClaimHollerNudge from "@/components/PostClaimHollerNudge";
import { firstWinConfetti } from "@/lib/confetti";
import { formatApiError } from "@/lib/api";

// -------------------------------------------------------------
// LANDING PAGE
// -------------------------------------------------------------
export function LootQuestLandingPage() {
  const [quests, setQuests] = useState(null);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/loot-quest");
      setQuests(data.loot_quests || []);
    })();
  }, []);

  return (
    <div className="py-8 space-y-8">
      <OGMeta
        title="Loot Quest · The Local Loot"
        description="Hunt physical chests. Enter the code. Claim your prize."
        url={typeof window !== "undefined" ? window.location.href : undefined}
      />

      <div className="loot-card p-6 md:p-10" style={{
        background: "radial-gradient(circle at 20% 0%, rgba(215,166,42,0.15), transparent 50%), #0F0F0F",
        borderColor: "#D7A62A",
      }}>
        <div className="overline flex items-center gap-1" style={{ color: "#D7A62A" }}>
          <Sparkles size={12} /> Mystery chest hunts
        </div>
        <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tight mt-3 leading-[0.95]">
          Loot <span style={{ color: "#D7A62A" }}>Quest</span>
        </h1>
        <p className="mt-4 max-w-2xl text-[#F5F2E8]/75">
          Scattered around town: physical treasure chests. Crack the clues, find the chest, snap a photo of the code inside. Enter it here to reveal what you won — anything from a $5 gift card to $500 cash.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/loot-quest/claim" className="btn-loot inline-flex items-center gap-2" data-testid="lq-claim-cta">
            <KeyRound size={16} /> Enter a code
          </Link>
          <Link to="/loot-quest/winners" className="btn-ghost inline-flex items-center gap-2" data-testid="lq-winners-cta">
            <Trophy size={16} /> See claimers
          </Link>
        </div>
      </div>

      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display uppercase text-2xl">Active quests</h2>
          <span className="font-mono text-xs text-[#F5F2E8]/50">
            {quests ? `${quests.length} chest${quests.length === 1 ? "" : "s"} hidden` : ""}
          </span>
        </div>
        {quests === null ? (
          <SkeletonGrid count={3} />
        ) : quests.length === 0 ? (
          <div className="loot-card-flat p-8 text-center text-[#F5F2E8]/60">
            No active quests right now. Check back soon — new chests drop often.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lq-active-grid">
            {quests.map((q) => (
              <Link
                key={q.id}
                to={`/loot-quest/${q.id}`}
                className="loot-card p-5 hover:border-[#D7A62A] transition"
                data-testid={`lq-card-${q.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="tag-pill text-[10px]" style={{ color: "#D7A62A", borderColor: "#D7A62A" }}>
                    <Lock size={10} className="inline mr-1" /> CHEST
                  </span>
                  <span className="font-mono text-xs text-[#F5F2E8]/50 flex items-center gap-1">
                    <MapPin size={12} /> {q.city}
                  </span>
                </div>
                <div className="font-display text-2xl uppercase mt-3 tracking-tight">{q.title}</div>
                {q.teaser && <p className="text-sm text-[#F5F2E8]/70 mt-2">{q.teaser}</p>}
                <div className="mt-3 flex items-center justify-between">
                  <PrizeTypeBadge drop={q} size="sm" />
                  <ArrowRight size={16} className="text-[#F5F2E8]/60" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// -------------------------------------------------------------
// DETAIL PAGE
// -------------------------------------------------------------
export function LootQuestDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(null);

  // Admin "preview as hunter" mode + sponsor preview mode (same plumbing as
  // DropDetailPage). Backend gates which one is allowed.
  const isAdminPreview = searchParams.get("preview") === "1";
  const previewToken = searchParams.get("preview_token") || "";
  const isPreview = isAdminPreview || !!previewToken;
  const previewParams = isAdminPreview
    ? { preview: 1 }
    : (previewToken ? { preview_token: previewToken } : null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/loot-quest/${id}`, previewParams ? { params: previewParams } : undefined);
        setQ(data.loot_quest);
      } catch {
        setQ(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (q === null) return <div className="py-10 text-[#F5F2E8]/60">Loading...</div>;
  if (q === false) return <div className="py-10 text-[#F5F2E8]/60">Quest not found.</div>;

  return (
    <div className="py-8 space-y-6">
      <OGMeta title={`${q.title} · Loot Quest`} description={q.teaser || "A hidden treasure chest in " + q.city} />
      <Link to="/loot-quest" className="font-mono text-xs text-[#F5F2E8]/50 hover:text-[#D7A62A]">← Back to Loot Quest</Link>

      {(isPreview || q.preview_mode) && (
        <div
          className="border-2 border-dashed p-3 flex items-start gap-3 text-sm"
          style={{ borderColor: "#D7A62A", background: "rgba(215,166,42,0.08)" }}
          data-testid="lq-preview-banner"
        >
          <span className="font-mono text-[10px] tracking-widest px-2 py-0.5 shrink-0"
            style={{ background: "#D7A62A", color: "#0F0F0F" }}>
            {previewToken ? "SPONSOR PREVIEW" : "PREVIEW"}
          </span>
          <div>
            <div className="font-display uppercase text-base leading-tight">
              {previewToken
                ? "Preview link — this is what hunters will see"
                : "You're viewing a draft Loot Quest as a hunter would"}
            </div>
            <div className="text-xs text-[#F5F2E8]/70 mt-1">
              {previewToken
                ? "Hunters can't find this URL. Clues, the map, and warmer/colder hints all work here. The chest unlocks for real hunters once the admin publishes it."
                : "Hunters can't see this URL. Clues, GPS hints, and the claim modal all work — but actual claims still require publishing first."}
            </div>
          </div>
        </div>
      )}

      <div className="loot-card p-6" style={{ borderColor: "#D7A62A" }}>
        <span className="font-mono text-xs text-[#F5F2E8]/50 flex items-center gap-1">
          <MapPin size={12} /> {q.city}
        </span>
        <h1 className="font-display uppercase text-4xl md:text-6xl tracking-tight mt-2 leading-[0.95]">{q.title}</h1>
        {q.teaser && <p className="mt-4 text-[#F5F2E8]/75">{q.teaser}</p>}
        <div className="mt-4"><PrizeTypeBadge drop={q} size="sm" /></div>
      </div>

      {q.zone && (
        <HuntZoneMap
          zone={q.zone}
          height={240}
          tint="#D7A62A"
          label="Chest zone"
          sub="Approximate area"
        />
      )}

      {q.gps_hints_available && (
        <GpsHintButton
          endpoint={`/loot-quest/${q.id}/hint`}
          testid="lq-hint-button"
          noun="chest"
          preview={isAdminPreview}
          previewToken={previewToken}
        />
      )}

      {q.clues && q.clues.length > 0 && (
        <section>
          <h2 className="font-display uppercase text-2xl mb-3">Clues</h2>
          <div className="space-y-2">
            {q.clues.sort((a, b) => a.order - b.order).map((c, i) => (
              <div key={i} className="loot-card-flat p-4">
                <div className="overline">Clue {i + 1}</div>
                <div className="mt-1 text-[#F5F2E8]/90">{c.text}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="pt-2">
        <Link to="/loot-quest/claim" className="btn-loot inline-flex items-center gap-2" data-testid="lq-enter-code">
          <KeyRound size={16} /> Found a chest? Enter the code
        </Link>
      </div>
    </div>
  );
}

// -------------------------------------------------------------
// CLAIM / CODE ENTRY PAGE
// -------------------------------------------------------------
export function LootQuestClaimPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState("");
  const [username, setUsername] = useState("");
  const [photo, setPhoto] = useState("");
  const [loading, setLoading] = useState(false);
  const [prize, setPrize] = useState(null);

  // Pre-fill from /claim handoff (?code=...). We accept whatever the URL
  // sent — backend is case-insensitive.
  useEffect(() => {
    const c = searchParams.get("code");
    if (c) setCode(c);
  }, [searchParams]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/loot-quest/claim", {
        code: code.trim(),
        username: username.trim() || undefined,
        photo_url: photo.trim() || undefined,
      });
      setPrize(data.prize);
      firstWinConfetti();
      toast.success("Prize unlocked!");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  if (prize) {
    const m = prizeMeta({ prize_type: prize.prize_type });
    const isCash = prize.prize_type === "cash" || !prize.prize_type;
    return (
      <div className="py-12 max-w-xl mx-auto text-center space-y-6">
        <OGMeta title="You claimed a Loot Quest chest!" description="The Local Loot" />
        <div className="overline" style={{ color: "#D7A62A" }}>You unlocked</div>
        <div className="font-display uppercase text-6xl md:text-7xl tracking-tight" style={{ color: m.color }}>
          {isCash ? `$${prize.prize_amount}` : m.label}
        </div>
        {!isCash && prize.prize_custom_label && (
          <div className="font-display text-2xl text-[#F5F2E8]/85">{prize.prize_custom_label}</div>
        )}
        {!isCash && prize.prize_amount > 0 && (
          <div className="font-mono text-sm text-[#F5F2E8]/50">(~${prize.prize_amount} value)</div>
        )}
        <p className="text-[#F5F2E8]/70">
          Nice work cracking <strong>{prize.title}</strong> in {prize.city}. We'll reach out about delivery — keep an eye on your email.
        </p>
        <div className="max-w-lg mx-auto text-left">
          <PostClaimHollerNudge
            name={username || ""}
            city={prize.city || ""}
            didWin={true}
            context={prize.title ? `the "${prize.title}" Loot Quest` : "this quest"}
          />
        </div>
        <div className="flex justify-center gap-2">
          <button onClick={() => navigate("/loot-quest")} className="btn-ghost">More quests</button>
          <button onClick={() => navigate("/loot-quest/winners")} className="btn-loot">See claimers</button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 max-w-xl mx-auto space-y-6">
      <OGMeta title="Enter a Loot Quest code" description="Unlock your prize" />
      <Link to="/loot-quest" className="font-mono text-xs text-[#F5F2E8]/50 hover:text-[#D7A62A]">← Back to Loot Quest</Link>
      <div>
        <div className="overline" style={{ color: "#D7A62A" }}>Code entry</div>
        <h1 className="font-display uppercase text-4xl tracking-tight mt-2">Unlock the chest</h1>
        <p className="mt-3 text-[#F5F2E8]/70">
          Found a chest? Type the code printed inside to reveal your prize.
        </p>
      </div>
      <form className="space-y-3" onSubmit={submit}>
        <input
          className="loot-input font-mono text-xl text-center tracking-widest"
          placeholder="CHEST CODE"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={40}
          autoFocus
          data-testid="lq-code"
        />
        <p className="text-[11px] text-[#F5F2E8]/50 text-center font-mono uppercase tracking-widest">
          UPPER or lower case — both work
        </p>
        <input
          className="loot-input"
          placeholder="@username (required if not signed in)"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          maxLength={40}
          data-testid="lq-username"
        />
        <input
          className="loot-input"
          placeholder="Photo of you + chest (optional)"
          value={photo}
          onChange={(e) => setPhoto(e.target.value)}
          data-testid="lq-photo"
        />
        <button type="submit" disabled={loading || !code.trim()} className="btn-loot w-full justify-center" data-testid="lq-submit">
          {loading ? "Verifying..." : "Unlock prize"}
        </button>
      </form>
    </div>
  );
}

// -------------------------------------------------------------
// WINNERS PAGE
// -------------------------------------------------------------
export function LootQuestWinnersPage() {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/loot-quest/winners");
        setWinners(data.winners || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  return (
    <div className="py-8 space-y-6">
      <OGMeta title="Loot Quest winners · The Local Loot" description="Every chest cracker." />
      <h1 className="font-display uppercase text-4xl tracking-tight">
        Chest <span style={{ color: "#D7A62A" }}>crackers</span>
      </h1>
      {loading ? (
        <SkeletonGrid count={6} />
      ) : winners.length === 0 ? (
        <div className="loot-card-flat p-8 text-center text-[#F5F2E8]/60">No claims yet. Be the first.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {winners.map((w) => {
            const m = prizeMeta(w);
            const isCash = (w.prize_type || "cash") === "cash";
            return (
              <div key={w.id} className="loot-card p-5">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs text-[#F5F2E8]/50 flex items-center gap-1">
                    <MapPin size={12} /> {w.city}
                  </span>
                  <PrizeTypeBadge drop={w} />
                </div>
                <div className="mt-3 font-display uppercase text-2xl">@{w.username}</div>
                <div className="mt-1 font-display text-xl" style={{ color: m.color }}>
                  {isCash ? `Claimed $${w.amount}` : `Claimed ${m.label}`}
                </div>
                {w.prize_custom_label && (
                  <div className="font-mono text-xs text-[#F5F2E8]/70 mt-0.5">{w.prize_custom_label}</div>
                )}
                {w.story && <p className="mt-3 text-sm text-[#F5F2E8]/70 italic">"{w.story}"</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
